import { ReactElement } from 'react'
import {
  Bar,
  BarChart,
  ComposedChart,
  Customized,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  usePlotArea,
  useYAxisScale,
  XAxis,
  YAxis,
} from 'recharts'
import { PillTabs } from 'components/FbUI/PillTabs'
import { cn } from 'components/UI/cn'
import { chartTheme } from '../widgets/chart-theme'
import { colors } from 'styles/tokens'
import { formatInteger } from 'utils/formatting'
import { seriesColor } from '../chart-palette'
import { QueryResult, ViewConfig } from '../view-config'

/**
 * A dashed target line at the report's saved threshold value, drawn on the
 * value axis (`y` for vertical bar/line, `x` for horizontal bar). Null when the
 * report has no threshold. `ifOverflow="extendDomain"` so a target beyond the
 * data still shows (the axis grows to include it). No text label — the dashed
 * target line is self-evident and a long label clips at the chart edge.
 */
function thresholdRef(
  threshold: ViewConfig['threshold'],
  axis: 'x' | 'y'
): ReactElement | null {
  if (!threshold) return null
  const props = {
    stroke: colors['fg'],
    strokeDasharray: '5 4',
    strokeWidth: 1.5,
    ifOverflow: 'extendDomain' as const,
  }
  return axis === 'y' ? (
    <ReferenceLine y={threshold.value} {...props} />
  ) : (
    <ReferenceLine x={threshold.value} {...props} />
  )
}

/**
 * Detail/builder chart for an aggregate QueryResult, in recharts (the dashboard
 * tiles use `DashboardTileChart`; both share `chartTheme`). Renders the encoding
 * given by `chartType` (defaults to `config.chart`):
 *   - 'bar'            single-series vertical bar
 *   - 'horizontal-bar' single-series horizontal bar (good for long labels)
 *   - 'stacked-bar'    stacked bar over series (categorical palette) + legend
 *   - 'line'           single-series line
 * Displays `config.chartMetric` (default: first metric). When `onMetricChange`
 * is supplied, a left-aligned pill switcher sits atop the chart card so the
 * metric is filtered in context.
 *
 * `compact` renders a minimal thumbnail (no switcher/axes/legend/tooltip) used
 * by the "Suggested views" gallery so each option previews the live data.
 *
 * The legend for stacked charts is rendered as our own row *below* the plot
 * (not recharts' in-SVG <Legend>, which steals height and squishes the bars
 * when there are many series).
 */

const TICK = { fill: chartTheme.axis, fontSize: 12 } as const

/**
 * Compact axis-tick labels so large values (revenue/volume in the millions)
 * stay short — "18.5M", "450k" — and don't clip at the chart's left edge the
 * way a full "18,489,476" does.
 */
const formatAxisTick = (value: number): string => {
  const n = Math.abs(value)
  if (n >= 1_000_000)
    return `${(value / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 1_000) return `${Math.round(value / 1_000)}k`
  return String(value)
}

type LegendItem = { label: string; color: string }

/** The chart encodings ReportChart can draw (everything but the table view). */
export type ReportChartType = Exclude<ViewConfig['chart'], 'table-only'>

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { name?: string; value: number; color?: string; fill?: string }[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-sm">
      {label != null && (
        <p className="mb-1 text-xs font-semibold text-fg-secondary">{label}</p>
      )}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span
            className="inline-block size-2 shrink-0 rounded-full"
            style={{
              backgroundColor: entry.color ?? entry.fill ?? chartTheme.primary,
            }}
          />
          <span className="text-sm font-medium text-fg">
            {entry.name ? `${entry.name}: ` : ''}
            {formatInteger(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

type EndSeries = { name: string; color: string; value: number }

/**
 * Direct end-of-line labels (Tufte / Eugene Wei "remove the legend"): instead of
 * a color key the reader cross-references, each series names itself at the right
 * end of its own line. Rendered as one `<Customized>` layer (not per-line
 * `LabelList`) so it can *dodge*: series whose lines end at near-equal values
 * would otherwise stack their labels on top of each other. We place each label
 * at its line's pixel y (via the chart's y-scale), then push overlapping labels
 * apart to a minimum gap — first downward, then clamp back up if the stack runs
 * past the plot's bottom edge. Rendered via `<Customized>`, so the chart's
 * y-scale and plot area come from recharts hooks (`useYAxisScale`/`usePlotArea`).
 * Single-axis multi-line only; the combo's right value axis would clash.
 */
const LABEL_GAP = 16

function EndLabels({ series }: { series: EndSeries[] }) {
  const scale = useYAxisScale()
  const plot = usePlotArea()
  if (!scale || !plot) return <></>
  const top = plot.y
  const bottom = plot.y + plot.height
  const x = plot.x + plot.width + 8

  const placed = series
    .map((s) => ({ ...s, y: Number(scale(s.value) ?? 0) }))
    .sort((a, b) => a.y - b.y)
  // Push each label below the previous one if they'd collide…
  for (let i = 1; i < placed.length; i++) {
    if (placed[i].y < placed[i - 1].y + LABEL_GAP)
      placed[i].y = placed[i - 1].y + LABEL_GAP
  }
  // …then, if the stack overflows the bottom, pull it back up from the bottom.
  const lastY = placed[placed.length - 1]?.y
  if (lastY != null && lastY > bottom) {
    placed[placed.length - 1].y = bottom
    for (let i = placed.length - 2; i >= 0; i--) {
      if (placed[i].y > placed[i + 1].y - LABEL_GAP)
        placed[i].y = placed[i + 1].y - LABEL_GAP
    }
  }

  return (
    <g>
      {placed.map((s) => (
        <text
          key={s.name}
          x={x}
          y={Math.max(top, s.y)}
          dy={4}
          fontSize={12}
          fontWeight={600}
          fill={s.color}
          textAnchor="start"
        >
          {s.name}
        </text>
      ))}
    </g>
  )
}

function ChartLegend({ items }: { items: LegendItem[] }) {
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-1.5">
          <span
            className="inline-block size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: it.color }}
          />
          <span className="text-xs font-medium text-fg-secondary">
            {it.label}
          </span>
        </div>
      ))}
    </div>
  )
}

export function ReportChart({
  config,
  result,
  onMetricChange,
  bare = false,
  fill = false,
  chartType,
  compact = false,
}: {
  config: ViewConfig
  result: QueryResult
  /** When provided (and not compact), renders the in-chart metric switcher. */
  onMetricChange?: (metricLabel: string) => void
  /** Render without the card chrome (border/shadow), to embed inside another card. */
  bare?: boolean
  /**
   * Fill the parent's height instead of using a fixed chart height. For
   * embedding in a sized container (a dashboard tile / grid cell). Off by
   * default so a standalone/preview chart keeps its own readable height — a
   * fill chart inside an unsized parent collapses to 0 (recharts then can't
   * measure and the chart overflows its column).
   */
  fill?: boolean
  /** Override the encoding to draw (else `config.chart`). Used by the gallery. */
  chartType?: ReportChartType
  /** Minimal thumbnail render (no switcher/axes/legend/tooltip). */
  compact?: boolean
}) {
  if (result.kind !== 'aggregate') return null
  const effective = chartType ?? config.chart
  // ranked is drawn by its own component (RankedListView).
  if (effective === 'table-only' || effective === 'ranked') return null

  const metricLabel = config.chartMetric ?? config.metrics![0].label
  const metrics = config.metrics ?? []
  const showSwitcher = !compact && !!onMetricChange && metrics.length > 0

  const singleData = result.groups.map((g) => ({
    name: g.key,
    value: g.metrics[metricLabel] ?? 0,
  }))

  // No axis titles: the chart's own title already reads "<measure> by
  // <dimension>", so axis titles just re-spend ink (Tufte). Tick labels carry
  // the categories; the value scale carries its own ticks.
  let chart: ReactElement
  let legendItems: LegendItem[] = []

  // Two measures with different units (e.g. Deals + Revenue) → a dual-axis
  // combo: the primary measure keeps the chosen encoding (bar or line) on the
  // left axis, the second (config.chartMetric2) rides a line on its own right
  // axis so neither scale crushes the other. Combo is opt-in (chartMetric2),
  // so templates that carry extra metrics only for their table stay single.
  const secondaryLabel =
    (effective === 'bar' ||
      effective === 'line' ||
      effective === 'horizontal-bar') &&
    !result.seriesKeys &&
    config.chartMetric2 &&
    config.chartMetric2 !== metricLabel &&
    metrics.some((m) => m.label === config.chartMetric2)
      ? config.chartMetric2
      : undefined

  if (secondaryLabel && effective === 'horizontal-bar') {
    // Horizontal dual-axis combo: categories on the Y axis, two value axes
    // (bottom = primary, top = secondary) so the two grouped bars per category
    // each read off their own scale.
    const primaryLabel = metricLabel
    const comboData = result.groups.map((g) => ({
      name: g.key,
      [primaryLabel]: g.metrics[primaryLabel] ?? 0,
      [secondaryLabel]: g.metrics[secondaryLabel] ?? 0,
    }))
    legendItems = compact
      ? []
      : [
          { label: primaryLabel, color: seriesColor(0) },
          { label: secondaryLabel, color: seriesColor(1) },
        ]
    chart = (
      <ComposedChart
        data={comboData}
        layout="vertical"
        margin={{ top: 4, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          xAxisId="bottom"
          type="number"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          tickFormatter={formatAxisTick}
        />
        <XAxis
          xAxisId="top"
          orientation="top"
          type="number"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          tickFormatter={formatAxisTick}
        />
        <YAxis
          type="category"
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={110}
        />
        {!compact && <Tooltip content={<ChartTooltip />} />}
        <Bar
          xAxisId="bottom"
          dataKey={primaryLabel}
          fill={seriesColor(0)}
          radius={[0, 6, 6, 0]}
          maxBarSize={compact ? 10 : 18}
        />
        <Bar
          xAxisId="top"
          dataKey={secondaryLabel}
          fill={seriesColor(1)}
          radius={[0, 6, 6, 0]}
          maxBarSize={compact ? 10 : 18}
        />
      </ComposedChart>
    )
  } else if (secondaryLabel) {
    const primaryLabel = metricLabel
    const comboData = result.groups.map((g) => ({
      name: g.key,
      [primaryLabel]: g.metrics[primaryLabel] ?? 0,
      [secondaryLabel]: g.metrics[secondaryLabel] ?? 0,
    }))
    legendItems = compact
      ? []
      : [
          { label: primaryLabel, color: seriesColor(0) },
          { label: secondaryLabel, color: seriesColor(1) },
        ]
    chart = (
      <ComposedChart
        data={comboData}
        margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          yAxisId="left"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && <Tooltip content={<ChartTooltip />} />}
        {/* Both measures use the chosen encoding (two bars, or two lines), each
            on its own axis so the larger scale can't crush the smaller. On a
            bar chart recharts clusters the two bars side by side per category. */}
        {effective === 'line' ? (
          <>
            <Line
              yAxisId="left"
              type="linear"
              dataKey={primaryLabel}
              stroke={seriesColor(0)}
              strokeWidth={2}
              dot={false}
            />
            <Line
              yAxisId="right"
              type="linear"
              dataKey={secondaryLabel}
              stroke={seriesColor(1)}
              strokeWidth={2}
              dot={false}
            />
          </>
        ) : (
          <>
            <Bar
              yAxisId="left"
              dataKey={primaryLabel}
              fill={seriesColor(0)}
              radius={[6, 6, 0, 0]}
              maxBarSize={compact ? 14 : 28}
            />
            <Bar
              yAxisId="right"
              dataKey={secondaryLabel}
              fill={seriesColor(1)}
              radius={[6, 6, 0, 0]}
              maxBarSize={compact ? 14 : 28}
            />
          </>
        )}
      </ComposedChart>
    )
  } else if (
    (effective === 'stacked-bar' || effective === 'grouped-bar') &&
    result.seriesKeys
  ) {
    const { seriesKeys } = result
    // Grouped = clustered bars side by side (no shared stackId); stacked =
    // segments piled in one bar. Same data + palette + legend otherwise.
    const grouped = effective === 'grouped-bar'
    legendItems = compact
      ? []
      : seriesKeys.map((sk, i) => ({
          label: sk,
          color: seriesColor(i),
        }))
    const data = result.groups.map((g) => {
      const row: Record<string, string | number> = { name: g.key }
      seriesKeys.forEach((sk) => {
        row[sk] = g.series?.[sk]?.[metricLabel] ?? 0
      })
      return row
    })
    chart = (
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && (
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={<ChartTooltip />}
          />
        )}
        {seriesKeys.map((sk, i) => (
          <Bar
            key={sk}
            dataKey={sk}
            name={sk}
            stackId={grouped ? undefined : 'a'}
            fill={seriesColor(i)}
            maxBarSize={compact ? 18 : grouped ? 24 : 36}
          />
        ))}
        {!compact && thresholdRef(config.threshold, 'y')}
      </BarChart>
    )
  } else if (
    (effective === 'line' || effective === 'dot') &&
    result.seriesKeys
  ) {
    // Multi-series line/dot over a time bucket: one series per value (e.g. one
    // per activity type across quarters). Same data shape as the stacked bar,
    // drawn as lines (or bare dots, when a connecting line over-implies
    // continuity between discrete counts). No bottom legend: each line names
    // itself at its right end (endSeriesLabel) — the extra right margin holds it.
    const asDots = effective === 'dot'
    const { seriesKeys } = result
    const data = result.groups.map((g) => {
      const row: Record<string, string | number> = { name: g.key }
      seriesKeys.forEach((sk) => {
        row[sk] = g.series?.[sk]?.[metricLabel] ?? 0
      })
      return row
    })
    const lastIndex = data.length - 1
    const endSeries: EndSeries[] = seriesKeys.map((sk, i) => ({
      name: sk,
      color: seriesColor(i),
      value: Number(data[lastIndex]?.[sk] ?? 0),
    }))
    chart = (
      <LineChart
        data={data}
        margin={{ top: 8, right: compact ? 8 : 96, left: 0, bottom: 8 }}
      >
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && <Tooltip content={<ChartTooltip />} />}
        {seriesKeys.map((sk, i) => {
          const color = seriesColor(i)
          return (
            <Line
              key={sk}
              type="linear"
              dataKey={sk}
              name={sk}
              stroke={asDots ? 'transparent' : color}
              strokeWidth={asDots ? 0 : 2}
              dot={asDots ? { r: 3, fill: color, strokeWidth: 0 } : false}
              activeDot={{ r: 4, fill: color, strokeWidth: 0 }}
            />
          )
        })}
        {!compact && (
          <Customized component={<EndLabels series={endSeries} />} />
        )}
        {!compact && thresholdRef(config.threshold, 'y')}
      </LineChart>
    )
  } else if (effective === 'dot') {
    // Single-series dot plot: bare points over the time axis, no connecting
    // line (which would over-imply continuity between discrete counts — Tufte).
    chart = (
      <LineChart
        data={singleData}
        margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && <Tooltip content={<ChartTooltip />} />}
        <Line
          type="linear"
          dataKey="value"
          stroke="transparent"
          strokeWidth={0}
          dot={{ r: 3.5, fill: chartTheme.primary, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: chartTheme.primary, strokeWidth: 0 }}
        />
        {!compact && thresholdRef(config.threshold, 'y')}
      </LineChart>
    )
  } else if (effective === 'line') {
    // Plain single-series line: a straight stroke connecting points, no dots, no fill
    // under it (the shading added ink without adding information).
    chart = (
      <LineChart
        data={singleData}
        margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && <Tooltip content={<ChartTooltip />} />}
        <Line
          type="linear"
          dataKey="value"
          stroke={chartTheme.primary}
          strokeWidth={2}
          dot={false}
        />
        {!compact && thresholdRef(config.threshold, 'y')}
      </LineChart>
    )
  } else if (effective === 'horizontal-bar') {
    chart = (
      <BarChart
        data={singleData}
        layout="vertical"
        margin={{ top: 4, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          type="number"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          tickFormatter={formatAxisTick}
        />
        <YAxis
          type="category"
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={110}
        />
        {!compact && (
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={<ChartTooltip />}
          />
        )}
        <Bar
          dataKey="value"
          fill={chartTheme.primary}
          radius={[0, 6, 6, 0]}
          maxBarSize={compact ? 14 : 28}
        />
        {!compact && thresholdRef(config.threshold, 'x')}
      </BarChart>
    )
  } else {
    // 'bar' (default)
    chart = (
      <BarChart
        data={singleData}
        margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
      >
        <XAxis
          dataKey="name"
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          dy={8}
          height={28}
        />
        <YAxis
          hide={compact}
          axisLine={{ stroke: chartTheme.grid }}
          tickLine={false}
          tick={TICK}
          width={44}
          tickFormatter={formatAxisTick}
        />
        {!compact && (
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={<ChartTooltip />}
          />
        )}
        <Bar
          dataKey="value"
          fill={chartTheme.primary}
          radius={[6, 6, 0, 0]}
          maxBarSize={compact ? 18 : 36}
        />
        {!compact && thresholdRef(config.threshold, 'y')}
      </BarChart>
    )
  }

  if (compact) {
    return (
      <div className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chart}
        </ResponsiveContainer>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col gap-3',
        // fill = embedded in a sized container (dashboard tile): take the cell's
        // height so the x-axis isn't clipped by its overflow-hidden. Otherwise a
        // fixed chart height (below). min-w-0 lets the chart shrink instead of
        // forcing horizontal page scroll.
        fill && 'h-full min-h-0',
        // bare = embedded in a host card that already pads the edge; its own p-4
        // would nest a second gutter and shrink the plot. Pad only when standalone.
        !bare && 'rounded-lg border border-line bg-surface p-4 shadow-sm'
      )}
    >
      {showSwitcher && (
        <PillTabs
          className="w-fit"
          idPrefix={`chart-metric-${config.id}`}
          aria-label="Chart metric"
          tabs={metrics.map((m) => ({ key: m.label, title: m.label }))}
          activeKey={metricLabel}
          onChange={onMetricChange}
        />
      )}
      <div className={cn('min-w-0', fill ? 'min-h-0 flex-1' : 'h-72')}>
        <ResponsiveContainer width="100%" height="100%">
          {chart}
        </ResponsiveContainer>
      </div>
      {legendItems.length > 0 && <ChartLegend items={legendItems} />}
    </div>
  )
}
